import { useEffect, useState } from 'react'
import { Modal } from '@/components/ui/Modal'
import { setupTokenOlustur, type OlusturulanToken } from '@/lib/services/setup-token-service'
import { useAuth } from '@/contexts/AuthContext'
import { Copy, RefreshCw, Smartphone, Check } from 'lucide-react'
import QRCode from 'qrcode'

interface Props {
  acik: boolean
  personelId: string | null
  personelIsim: string
  onKapat: () => void
}

export function SetupTokenModal({ acik, personelId, personelIsim, onKapat }: Props) {
  const { firebaseUser } = useAuth()
  const [token, setToken] = useState<OlusturulanToken | null>(null)
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null)
  const [yukleniyor, setYukleniyor] = useState(false)
  const [hata, setHata] = useState<string | null>(null)
  const [kopyalandi, setKopyalandi] = useState(false)

  async function tokenOlustur() {
    if (!personelId || !firebaseUser) return
    setYukleniyor(true)
    setHata(null)
    try {
      const yeniToken = await setupTokenOlustur(personelId, firebaseUser.uid)
      setToken(yeniToken)
      // QR kodunu üret
      const dataUrl = await QRCode.toDataURL(yeniToken.url, {
        width: 320,
        margin: 2,
      })
      setQrDataUrl(dataUrl)
    } catch (err) {
      console.error(err)
      setHata('Token oluşturulamadı: ' + (err as Error).message)
    } finally {
      setYukleniyor(false)
    }
  }

  // Modal açıldığında otomatik token üret
  useEffect(() => {
    if (acik && personelId && !token) {
      tokenOlustur()
    }
    // Modal kapandığında temizle
    if (!acik) {
      setToken(null)
      setQrDataUrl(null)
      setKopyalandi(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acik, personelId])

  function urlKopyala() {
    if (!token) return
    navigator.clipboard.writeText(token.url)
    setKopyalandi(true)
    setTimeout(() => setKopyalandi(false), 2000)
  }

  return (
    <Modal acik={acik} onKapat={onKapat} baslik="Personel Kurulum QR'ı" boyut="md">
      <div className="space-y-4">
        <div className="text-center">
          <Smartphone className="w-12 h-12 text-brand-600 mx-auto mb-2" />
          <p className="text-sm text-gray-600">
            <strong>{personelIsim}</strong> bu QR'ı kendi telefonunda okutarak sisteme bağlanır.
          </p>
        </div>

        {yukleniyor && (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
            <p className="mt-3 text-sm text-gray-600">QR oluşturuluyor...</p>
          </div>
        )}

        {hata && (
          <div className="bg-red-50 border border-red-200 text-red-800 text-sm rounded-xl p-3">
            {hata}
          </div>
        )}

        {token && qrDataUrl && (
          <>
            {/* QR */}
            <div className="bg-white border-2 border-gray-200 rounded-2xl p-4 flex justify-center">
              <img src={qrDataUrl} alt="Setup QR" className="w-64 h-64" />
            </div>

            {/* URL kopyala */}
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-3">
              <p className="text-xs font-medium text-gray-700 mb-1.5">veya bu linki paylaş:</p>
              <div className="flex gap-2">
                <input
                  type="text"
                  readOnly
                  value={token.url}
                  className="input-base flex-1 text-xs"
                  onFocus={e => e.currentTarget.select()}
                />
                <button
                  onClick={urlKopyala}
                  className="px-3 py-2 bg-brand-600 text-white rounded-xl hover:bg-brand-700 transition-colors flex items-center gap-1"
                >
                  {kopyalandi ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  <span className="text-sm">{kopyalandi ? 'Kopyalandı' : 'Kopyala'}</span>
                </button>
              </div>
            </div>

            {/* Geçerlilik */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
              <p className="text-xs text-amber-900">
                ⏱️ Bu QR <strong>{token.gecerlilikTarihi.toLocaleString('tr-TR')}</strong> tarihine kadar geçerli (48 saat).
              </p>
              <p className="text-xs text-amber-800 mt-1">
                ⚠️ Tek kullanımlıktır. Personel başka bir cihazdan kurulum yaparsa yeni QR oluşturmalısın.
              </p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
              <p className="text-xs text-blue-900">
                💡 <strong>Faz 1.4'te aktif olacak:</strong> Personel bu QR'ı okuduğunda telefonu sisteme bağlanacak ve kullanmaya başlayabilecek. Şu an sadece QR üretimi çalışıyor.
              </p>
            </div>
          </>
        )}

        <div className="flex gap-2 pt-2">
          <button
            onClick={tokenOlustur}
            disabled={yukleniyor || !personelId}
            className="btn-secondary flex-1 flex items-center justify-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Yeni QR Oluştur</span>
          </button>
          <button onClick={onKapat} className="btn-primary flex-1">
            Tamam
          </button>
        </div>
      </div>
    </Modal>
  )
}
